// Pure eligibility engine for market lifecycle modals
// (spec 112-market-lifecycle-modals). Given the page's already-loaded market +
// position signals, decides whether — and which — lifecycle modal should show.
// No React, no side effects: the trigger hook supplies `nowMs` and consults the
// seen-store; this module is a pure function of its inputs so it is fully
// unit-testable.

import { isDustD6 } from '@/lib/validation/dust';
import type {
  LifecycleClaimState,
  LifecycleModalPayload,
  LifecycleStage,
  LifecycleVariant,
} from '@/stores/market-lifecycle-modal';

/** Stake-less variants only surface within this window of the event (FR-006, RD-3). */
export const RECENCY_WINDOW_MS: number = 7 * 24 * 60 * 60 * 1000;

export interface EligibilityHolding {
  outcomeIndex: number;
  /** D6-scaled balance string. */
  balance: string;
  /**
   * D6-scaled realized-PnL string. A losing position is drained to a `0`
   * balance once `allLosersDrained` runs, but the realized loss persists here —
   * so a non-zero value on a non-winning outcome still marks a held-and-lost
   * position. Optional: callers without PnL context (e.g. graduation) may omit
   * it, in which case it is treated as `0`.
   */
  realizedPnl?: string;
}

export interface EligibilityInputs {
  isAuthenticated: boolean;
  walletAddress: string | null | undefined;
  marketPubkey: string;
  /** Current market status (`backing` | `trading` | `resolved` | `graduated` | …). */
  status: string;
  /** Previous observed status — `null` before the first observation. */
  prevStatus: string | null;
  winnerOutcomeIndex: number | null;
  /** `resolvedAt` parsed to epoch ms; `null` when absent / unparseable. */
  resolvedAtMs: number | null;
  /** Wall-clock epoch ms — supplied by the hook's effect (never read in render). */
  nowMs: number;
  holdings: EligibilityHolding[];
  isBacker: boolean;
  claim: LifecycleClaimState | null;
  winningOutcomeLabel: string | null;
  winningOutcomeImageUrl: string | null;
  winningOutcomeImageNsfw: boolean;
}

/**
 * Stable seen-key. Uses the MARKET pubkey (not the winner mint) so it is
 * identical whether the user landed on `/markets/[pubkey]` or was redirected to
 * `/winners/[mint]` (EC-6).
 */
export function seenKey(wallet: string, market: string, stage: LifecycleStage): string {
  return `${wallet}:${market}:${stage}`;
}

/** Freshness check for stake-less variants (FR-006). */
export function isFresh(eventMs: number | null, nowMs: number): boolean {
  if (eventMs === null) return false;
  return nowMs - eventMs <= RECENCY_WINDOW_MS;
}

function holdsNonDustWinning(holdings: EligibilityHolding[], winnerIndex: number): boolean {
  return holdings.some((h) => h.outcomeIndex === winnerIndex && !isDustD6(h.balance));
}

/**
 * A real losing position on a non-winning outcome: the user either still holds
 * it (non-dust balance) OR realized a non-zero PnL there. The PnL arm is what
 * matters post-drain — once `allLosersDrained` zeroes a loser's balance, the
 * loss survives only in `realizedPnl`; without checking it, drained losers fall
 * through to `resolution-not-holder` (the generic "you weren't holding a
 * position" copy). Mirrors the resolved-panel filter in `market-detail-layout`.
 * The winner outcome is excluded so a claimed winner (winning balance burned to
 * 0, positive realizedPnl) is never misread as a loss.
 */
function heldLosingPosition(holdings: EligibilityHolding[], winnerIndex: number): boolean {
  return holdings.some(
    (h) =>
      h.outcomeIndex !== winnerIndex &&
      (!isDustD6(h.balance) || Number(h.realizedPnl ?? '0') !== 0),
  );
}

/**
 * Resolution variant from the two axes: outcome position (won / lost / none) ×
 * backer status. Dust balances count as non-holdings (FR-013) — except a
 * non-winning outcome with non-zero realized PnL, which still counts as a held
 * loss (covers the post-drain zero-balance loser).
 */
export function classifyResolution(
  winnerOutcomeIndex: number,
  holdings: EligibilityHolding[],
  isBacker: boolean,
): LifecycleVariant {
  if (holdsNonDustWinning(holdings, winnerOutcomeIndex)) {
    return isBacker ? 'resolution-won-backer' : 'resolution-won-nonbacker';
  }
  if (heldLosingPosition(holdings, winnerOutcomeIndex)) {
    return isBacker ? 'resolution-lost-backer' : 'resolution-lost-nonbacker';
  }
  return 'resolution-not-holder';
}

function isResolvedStatus(status: string): boolean {
  return status === 'resolved' || status === 'graduated';
}

function resolutionPayload(
  inputs: EligibilityInputs,
  variant: LifecycleVariant,
): LifecycleModalPayload {
  return {
    marketPubkey: inputs.marketPubkey,
    stage: 'resolution',
    variant,
    isBacker: inputs.isBacker,
    winnerOutcomeIndex: inputs.winnerOutcomeIndex,
    winningOutcomeLabel: inputs.winningOutcomeLabel,
    winningOutcomeImageUrl: inputs.winningOutcomeImageUrl,
    winningOutcomeImageNsfw: inputs.winningOutcomeImageNsfw,
    claim: inputs.claim,
  };
}

function graduationPayload(
  inputs: EligibilityInputs,
  variant: LifecycleVariant,
): LifecycleModalPayload {
  return {
    marketPubkey: inputs.marketPubkey,
    stage: 'graduation',
    variant,
    isBacker: inputs.isBacker,
    winnerOutcomeIndex: null,
    winningOutcomeLabel: null,
    winningOutcomeImageUrl: null,
    winningOutcomeImageNsfw: false,
    claim: null,
  };
}

/**
 * Returns the payload to show, or `null`. Does NOT consult the seen-set — the
 * hook owns the seen check so this stays a pure function of its inputs.
 * Resolution takes precedence over graduation (FR-007).
 */
export function computeEligibility(inputs: EligibilityInputs): LifecycleModalPayload | null {
  if (!inputs.isAuthenticated || !inputs.walletAddress) {
    return null;
  }

  // Resolution is a celebration of the live event, never a cold-load prompt.
  // The previous status must come from a real market observation; the trigger
  // deliberately leaves it null while the initial market query is pending.
  const liveResolution =
    inputs.prevStatus !== null &&
    !isResolvedStatus(inputs.prevStatus) &&
    isResolvedStatus(inputs.status);

  // Resolution stage (precedence).
  if (inputs.winnerOutcomeIndex !== null && liveResolution) {
    const variant = classifyResolution(inputs.winnerOutcomeIndex, inputs.holdings, inputs.isBacker);
    // Stake-less = a non-holder who didn't back. Gate on freshness (FR-006);
    // holders and backers fire regardless of how long ago the market resolved.
    const stakeLess = variant === 'resolution-not-holder' && !inputs.isBacker;
    if (stakeLess && !isFresh(inputs.resolvedAtMs, inputs.nowMs)) {
      return null;
    }
    return resolutionPayload(inputs, variant);
  }

  // Graduation stage (backing → trading).
  const liveTransition = inputs.prevStatus === 'backing' && inputs.status === 'trading';
  const backerColdView = inputs.isBacker && inputs.status === 'trading';
  if (liveTransition || backerColdView) {
    const variant: LifecycleVariant = inputs.isBacker ? 'graduation-backer' : 'graduation-generic';
    // No graduation timestamp exists (RD-2): the generic (stake-less) variant
    // only fires on a live transition, never on a cold first view.
    if (variant === 'graduation-generic' && !liveTransition) {
      return null;
    }
    return graduationPayload(inputs, variant);
  }

  return null;
}
